import { BrButton, BrLoading, BrSelect } from '@govbr-ds/react-components';
import { SelectOptions } from '@govbr-ds/react-components/dist/components/BrSelect';
import { useQuery } from '@tanstack/react-query';
import InfoCircleTooltip from 'components/infoCircleTooltip/infoCircleTooltip';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { IMunicipio } from 'interfaces/IMunicipio';
import { IProcessoMinerario } from 'interfaces/IProcessoMinerario';
import { Dispatch, ReactNode, SetStateAction, useEffect, useId, useState } from 'react';
import style from './anmSelectQuery.module.scss';

export interface IAnmSelectQueryProps {
  label: string;
  value: string | number;
  setValue: Function;
  functionQuery: Function;
  keyQuery: any[];
  formatResult?: Function;
  placeholder?: string;
  errorMessage?: string;
  id?: string;
  isDisabled?: boolean;
  customClassName?: string;
  enableQuery?: boolean;
  isPlaceHolderMunicipio?: boolean;
  setLabel?: Function;
  defaultValue?: string;
  setRetornoQuery?: Dispatch<SetStateAction<any[]>>;
  hintText?: ReactNode;
}

const AnmSelectQuery = ({
  label,
  value,
  setValue,
  functionQuery,
  keyQuery,
  formatResult,
  placeholder = 'Selecionar',
  errorMessage = 'Não foi possível carregar os dados',
  id,
  isDisabled = false,
  customClassName = '',
  enableQuery = true,
  isPlaceHolderMunicipio = false,
  setLabel,
  defaultValue,
  hintText,
  setRetornoQuery,
}: IAnmSelectQueryProps) => {
  const [selectOptions, setSelectOptions] = useState<SelectOptions[]>([]);

  const componentId = useId();
  const testId = id ?? `anm-select-query-${componentId}`;

  const {
    data: queryResult,
    isLoading,
    error: queryError,
    refetch: refetchQuery,
  } = useQuery<any[], IErrorWithSentryId>({
    queryKey: keyQuery,
    queryFn: () => functionQuery(),
    refetchOnWindowFocus: false,
    enabled: enableQuery,
  });

  useEffect(() => {
    let options = [];

    if (queryResult == undefined) {
      return;
    }

    if (setRetornoQuery) {
      setRetornoQuery(queryResult);
    }

    if (formatResult) {
      options = formatResult(queryResult);
    } else {
      options = queryResult.map((value) => ({
        label: value.toString(),
        value: value.toString(),
      }));
    }

    if (options.length === 0) {
      options.push({ label: 'Não foram localizados itens para esta seleção.', value: '' });
    }

    if (defaultValue) {
      options.unshift({ label: defaultValue, value: '-' });
    }

    setSelectOptions(options);
  }, [queryResult, queryError]);

  const placeHolderMunicipio = () => {
    return (
      (queryResult as unknown as IProcessoMinerario)?.municipios?.find(
        (municipio: IMunicipio) => municipio.codigoIBGE.toString() === value.toString()
      )?.nome ?? 'Selecione o município'
    );
  };

  return (
    <div className={style.anmSelectContainer}>
      <div className={style.anmSelectLabel}>
        <label data-testid='select-label'>{label}</label>
        {hintText !== '' && hintText !== undefined && (
          <div className={style.situacaoTooltipContainer}>
            <InfoCircleTooltip text={hintText} id={`anm-select-query-hint-${componentId}`} customPlace='right' />
          </div>
        )}
      </div>

      {isLoading === true && <BrLoading />}
      {queryError == null && isLoading === false && (
        <form autoComplete='off'>
          <BrSelect
            id={testId}
            data-testid={testId}
            key={'select-' + value.toString().charAt(0).toUpperCase() + value.toString().slice(1)}
            className={`${style.select} ${customClassName}`}
            placeholder={isPlaceHolderMunicipio ? placeHolderMunicipio() : placeholder}
            value={value}
            onChange={(value: number | string) => {
              setValue(value);
              if (setLabel) {
                setLabel(selectOptions.find((option) => option.value == value)?.label ?? '');
              }
            }}
            options={selectOptions}
            disabled={isDisabled}
            defaultValue={defaultValue}
          />
        </form>
      )}
      {queryError && (
        <div className={style.errorContainer}>
          <span className={style.errorText}>{errorMessage}</span>
          <BrButton size='small' onClick={() => refetchQuery()}>
            (Tentar novamente)
          </BrButton>
        </div>
      )}
    </div>
  );
};

export default AnmSelectQuery;
